import React, { useState } from "react";
// import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Divider,
  Card,
} from "@mui/material";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import Logo from "../../components/Logo";
import LoginForm from "./LoginForm";
import LostPasswordForm from "./LostPasswordForm";

const Login = () => {

  const [isLoginForm, setIsLoginForm ] = useState(true);

  // Beautiful blue gradient background with ieQip features
  const RootStyle = styled("div")({
    background: `
      linear-gradient(
        135deg,
        #1e88e5 0%,
        #1976d2 20%,
        #1565c0 40%,
        #0d47a1 60%,
        #0277bd 80%,
        #01579b 100%
      )
    `,
    minHeight: "100vh",
    width: "100vw",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("/assets/iEqipLoginBackground.png")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      opacity: 0.05,
      zIndex: 0
    }
  });

  const HeadingStyle = styled(Box)({
    textAlign: "center",
    marginBottom: '16px'
  });

  const ContentStyle = styled(Card)({
    maxWidth: 480,
    width: '100%',
    padding: 48,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.2)',
    borderRadius: 24,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    background: "rgba(255, 255, 255, 0.98)",
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    margin: '20px',
    position: 'relative',
    zIndex: 10,
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 30px 80px rgba(0,0,0,0.35), 0 12px 40px rgba(0,0,0,0.25)',
      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    },
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  });

  const FeatureContainer = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    pointerEvents: 'none',
    '@media (max-width: 768px)': {
      display: 'none'
    }
  });

  const FeaturePosition = styled(Box)({
    position: 'absolute',
    width: '320px',
    '@media (max-width: 1400px)': {
      width: '280px'
    },
    '@media (max-width: 1200px)': {
      width: '240px'
    }
  });

  const FeatureCard = styled(Box)({
    background: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    color: 'white',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.5,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    animation: 'fadeInScale 0.6s ease-out forwards',
    opacity: 0,
    transform: 'scale(0.9)',
    width: '100%',
    maxWidth: '320px',
    height: '100px', // Fixed height for consistency
    '@keyframes fadeInScale': {
      to: {
        opacity: 0.95,
        transform: 'scale(1)'
      }
    },
    '@media (max-width: 1400px)': {
      padding: '20px',
      fontSize: '15px',
      height: '90px',
      maxWidth: '280px'
    },
    '@media (max-width: 1200px)': {
      padding: '18px',
      fontSize: '14px',
      height: '80px',
      maxWidth: '240px',
      gap: '14px'
    }
  });

  const FeatureIcon = styled(Box)({
    minWidth: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    flexShrink: 0,
    '@media (max-width: 1400px)': {
      minWidth: '44px',
      height: '44px',
      fontSize: '22px'
    },
    '@media (max-width: 1200px)': {
      minWidth: '40px',
      height: '40px',
      fontSize: '20px'
    }
  });

  let easing = [0.6, -0.05, 0.01, 0.99];
  const fadeInUp = {
    initial: {
      y: 60,
      opacity: 0,
      transition: { duration: 0.6, ease: easing },
    },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: easing,
      },
    },
  };
  const features = [
    {
      icon: '🔒',
      text: '100% electronic platform with secure login'
    },
    {
      icon: '🌍',
      text: 'View inspections in real time from anywhere to track progress instantly'
    },
    {
      icon: '👥',
      text: 'Auditors can collaborate and see each other\'s work in real time'
    },
    {
      icon: '🤖',
      text: 'AI delivers instant answers and references to support FDA inspections and prevent future deficiencies'
    },
    {
      icon: '📋',
      text: 'Mock FDA-483 can be generated daily and at the end of each inspection'
    },
    {
      icon: '✍️',
      text: 'Create mock inspection reports on the go—generate a draft instantly at close-out'
    },
    {
      icon: '🏢',
      text: 'Can be brought in-house as an internal audit tool'
    },
    {
      icon: '📚',
      text: 'Used for training to learn how regulators think and what they look for'
    },
    {
      icon: '⚠️',
      text: 'Auditors can rate findings (OAI, VAI, NAI) to alert management, especially for supplier audits'
    },
    {
      icon: '💊',
      text: 'Not just for pharma—also for medical devices, APIs, supplements, and more'
    }
  ];

  return (
    <RootStyle>
      <FeatureContainer>
        {/* Top Left */}
        <FeaturePosition sx={{ top: '40px', left: '40px' }}>
          <FeatureCard>
            <FeatureIcon>{features[0].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[0].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Top Right */}
        <FeaturePosition sx={{ top: '40px', right: '40px' }}>
          <FeatureCard>
            <FeatureIcon>{features[1].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[1].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Middle Left */}
        <FeaturePosition sx={{ top: '50%', left: '40px', transform: 'translateY(-50%)' }}>
          <FeatureCard>
            <FeatureIcon>{features[2].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[2].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Middle Right */}
        <FeaturePosition sx={{ top: '50%', right: '40px', transform: 'translateY(-50%)' }}>
          <FeatureCard>
            <FeatureIcon>{features[3].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[3].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Bottom Left */}
        <FeaturePosition sx={{ bottom: '40px', left: '40px' }}>
          <FeatureCard>
            <FeatureIcon>{features[4].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[4].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Bottom Right */}
        <FeaturePosition sx={{ bottom: '40px', right: '40px' }}>
          <FeatureCard>
            <FeatureIcon>{features[5].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[5].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        {/* Additional features for larger screens */}
        <FeaturePosition sx={{ 
          top: '20%', 
          left: '40px',
          '@media (max-width: 1400px)': { display: 'none' }
        }}>
          <FeatureCard>
            <FeatureIcon>{features[6].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[6].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        <FeaturePosition sx={{ 
          top: '20%', 
          right: '40px',
          '@media (max-width: 1400px)': { display: 'none' }
        }}>
          <FeatureCard>
            <FeatureIcon>{features[7].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[7].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        <FeaturePosition sx={{ 
          bottom: '20%', 
          left: '40px',
          '@media (max-width: 1400px)': { display: 'none' }
        }}>
          <FeatureCard>
            <FeatureIcon>{features[8].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[8].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>

        <FeaturePosition sx={{ 
          bottom: '20%', 
          right: '40px',
          '@media (max-width: 1400px)': { display: 'none' }
        }}>
          <FeatureCard>
            <FeatureIcon>{features[9].icon}</FeatureIcon>
            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              {features[9].text}
            </Typography>
          </FeatureCard>
        </FeaturePosition>
      </FeatureContainer>
      
      <Container maxWidth="sm" disableGutters>
        <ContentStyle>
          <HeadingStyle component={motion.div} {...fadeInUp}>
            <Logo />
            {isLoginForm ? (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ 
                  color: "#1976d2", 
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  lineHeight: 1.4,
                  textShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                }}>
                  Are you equipped for inspections?
                </Typography>
                <Typography sx={{ 
                  fontSize: '1.6rem',
                  fontWeight: 900,
                  letterSpacing: '1px',
                  mt: 0.5,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: 'none',
                  '& span': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 900,
                    fontSize: '1.1em',
                    letterSpacing: '2px',
                    display: 'inline-block',
                    animation: 'shimmer 3s ease-in-out infinite',
                    '@keyframes shimmer': {
                      '0%, 100%': { 
                        filter: 'brightness(1)' 
                      },
                      '50%': { 
                        filter: 'brightness(1.3)' 
                      }
                    }
                  }
                }}>
                  YES, <Box component="span">"ieQip"</Box>
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ 
                color: "#37474f", 
                mt: 2, 
                fontSize: '1.2rem',
                fontWeight: 500,
                letterSpacing: '0.5px'
              }}>
                Reset Your Password
              </Typography>
            )}
            <Typography sx={{ 
              color: "#546e7a", 
              mt: 1, 
              fontSize: '1rem',
              fontWeight: 400,
              opacity: 0.9
            }}>
            {isLoginForm ? "Intelligent Electronic Quality Inspection Platform" : "Enter your email to reset your password"}
            </Typography>
          </HeadingStyle>

          <Divider
            sx={{ 
              my: 4,
              '&::before, &::after': {
                borderColor: 'rgba(55, 71, 79, 0.12)'
              }
            }}
            component={motion.div}
            {...fadeInUp}
          ></Divider>

          {isLoginForm ? <LoginForm setIsLoginForm={setIsLoginForm}/> : <LostPasswordForm setIsLoginForm={setIsLoginForm}/>}

          {/* <Typography
            component={motion.p}
            {...fadeInUp}
            variant="body2"
            align="center"
            sx={{ mt: 3 }}
          >
            Don't have an account?{" "}
            <Link variant="subtitle2" component={RouterLink} to="/signup">
              Sign up
            </Link>
          </Typography> */}
        </ContentStyle>
      </Container>
    </RootStyle>
  );
};

export default Login;
